import dayjs, { Dayjs } from "dayjs";
import { TJobDisplayColumnSetting } from "@/features/job/components/job-list/job-filter-shared";

// Re-export for convenience since the column type is shared with main job list.
export type { TJobDisplayColumnSetting } from "@/features/job/components/job-list/job-filter-shared";

const buildScopedKey = (base: string, companyId?: number) => (companyId !== undefined && companyId !== null ? `${base}_${companyId}` : base);

export const getPastJobsDisplayColumnsKey = (companyId?: number) => buildScopedKey("past_jobs_display_columns", companyId);
export const getPastJobsAdvancedFiltersKey = (companyId?: number) => buildScopedKey("past_jobs_advanced_filters", companyId);
export const getPastJobsAppliedConditionNameKey = (companyId?: number) => buildScopedKey("past_jobs_applied_condition_name", companyId);
export const getPastJobsAppliedConditionIdKey = (companyId?: number) => buildScopedKey("past_jobs_applied_condition_id", companyId);
export const getPastJobsDefaultSeededKey = (companyId?: number) => buildScopedKey("past_jobs_default_seeded", companyId);

export const getPastJobsPageType = (companyId?: number) => (companyId !== undefined && companyId !== null ? `past_job_${companyId}` : "past_job");

/** Default table columns for past jobs modal: 求人名, ステータス, 総支給額, 勤務場所, 応募数, 募集人数, 募集期限 */
export const defaultPastJobsDisplayColumns: TJobDisplayColumnSetting[] = [
  { id: "jobName", label: "求人名", visible: true, order: 1 },
  { id: "status", label: "ステータス", visible: true, order: 2 },
  { id: "totalAmount", label: "総支給額", visible: true, order: 3 },
  { id: "workplace", label: "勤務場所", visible: true, order: 4 },
  { id: "caseCount", label: "応募数", visible: true, order: 5 },
  { id: "recruitmentNumber", label: "募集人数", visible: true, order: 6 },
  { id: "endDate", label: "募集期限", visible: true, order: 7 },
];

/** Column IDs that cannot be hidden (always displayed). */
export const PAST_JOBS_LOCKED_COLUMN_IDS = ["jobName", "status"];

export type TPastJobsFilterForm = {
  jobName: string;
  status: string[];
  totalAmount: number | string;
  workplace: string;
  caseCount: number | string;
  recruitmentNumber: number | string;
  endDateFrom: Dayjs | null;
  endDateTo: Dayjs | null;
};

export const defaultPastJobsFilterValues: TPastJobsFilterForm = {
  jobName: "",
  status: [],
  totalAmount: "",
  workplace: "",
  caseCount: "",
  recruitmentNumber: "",
  endDateFrom: null,
  endDateTo: null,
};

/** Multi-select filter field keys (currently only status). */
export const PAST_JOBS_MULTI_SELECT_FILTER_KEYS: (keyof TPastJobsFilterForm)[] = ["status"];

export const isEmptyPastJobsFilterValue = (value: unknown): boolean => {
  if (value === "" || value === null || value === undefined) return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
};

export const stripEmptyPastJobsFilterFields = (filters: TPastJobsFilterForm): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(filters)) {
    if (isEmptyPastJobsFilterValue(value)) continue;
    result[key] = value;
  }
  return result;
};

/** Normalize date fields (string/unknown → Dayjs) for DatePicker compatibility. */
export const normalizePastJobsDateFields = (filters: Partial<TPastJobsFilterForm>): Partial<TPastJobsFilterForm> => {
  const result = { ...filters };
  if (result.endDateFrom && typeof result.endDateFrom === "string") {
    const day = dayjs(result.endDateFrom);
    result.endDateFrom = day.isValid() ? day : null;
  }
  if (result.endDateTo && typeof result.endDateTo === "string") {
    const day = dayjs(result.endDateTo);
    result.endDateTo = day.isValid() ? day : null;
  }
  return result;
};

/** Normalize legacy scalar values to arrays for back-compat with earlier saves. */
export const normalizePastJobsMultiSelectFields = (filters: Partial<Record<string, unknown>>): Partial<TPastJobsFilterForm> => {
  const result = { ...filters };
  for (const key of PAST_JOBS_MULTI_SELECT_FILTER_KEYS) {
    const value = result[key];
    if (value === undefined) continue;
    if (Array.isArray(value)) continue;
    if (value === "" || value === null) {
      result[key] = [];
    } else {
      result[key] = [String(value)];
    }
  }
  return result as Partial<TPastJobsFilterForm>;
};
